import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useField, useFormikContext } from 'formik';
import {
  colors,
  InputAdornment,
  Stack,
  TextField,
  Typography
} from '@mui/material';
import {
  AccessTime as AccessTimeIcon,
  Event as EventIcon
} from '@mui/icons-material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import { DatePicker } from '@mui/x-date-pickers';
import { DATE_FORMAT } from 'src/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';
import { getShiftDetails } from 'src/redux/settingSlice';
import { getStoredUserTimezone, parseInStoredTimezone } from 'src/utils/timezoneUtils';

export default function DatePickerField({
  label,
  name,
  disabled,
  dateFormat,
  onlyDate = false,
  displayFormat,
  disableShiftPrefill = false,
  disableOpenPicker = false,
  nativeInput = false,
  required = false,
  requiredHintText = 'This field is required',
  ...rest
}) {
  const dispatch = useDispatch();
  const { shiftDetails, shiftLoaded } = useSelector((state) => state.settings);
  const currentShift = shiftDetails[0];
  const formikProps = useFormikContext();
  const [field, meta] = useField(name);
  const hasInitializedDefault = useRef(false);
  const { value: selectedValue } = field;
  const userTimezone = getStoredUserTimezone();
  const shouldPrefillFromShift = !disableShiftPrefill && (name?.includes('gte') || name?.includes('lte'));
  let dateFormatSting = onlyDate ? DATE_FORMAT.unixDate : DATE_FORMAT.unixDateTime;
  if (dateFormat) {
    dateFormatSting = dateFormat;
  }
  const pickerDisplayFormat = displayFormat || (onlyDate ? DATE_FORMAT.date : DATE_FORMAT.dateTime);
  const nativeFormat = onlyDate ? DATE_FORMAT.unixDate : 'YYYY-MM-DDTHH:mm';
  const {
    InputProps: restInputProps,
    InputLabelProps: restInputLabelProps,
    inputProps: restInputAttributes,
    sx: restSx
  } = rest;

  const formatNativeValue = (value) => {
    if (!value) return '';
    const parsed = parseInStoredTimezone(value, dateFormatSting);
    if (!parsed.isValid()) return '';
    return parsed.format(nativeFormat);
  };

  const handleNativeChange = (event) => {
    const rawValue = event.target.value;
    if (!rawValue) {
      formikProps.setFieldValue(name, null, true);
      return;
    }

    const parsed = moment(rawValue);
    if (!parsed.isValid()) {
      formikProps.setFieldValue(name, null, true);
      return;
    }

    formikProps.setFieldValue(name, parsed.format(dateFormatSting), true);
  };

  const formatNativeBoundary = (value) => {
    if (!value) return '';
    const parsed = parseInStoredTimezone(value, dateFormatSting);
    if (!parsed.isValid()) return '';
    return parsed.format(nativeFormat);
  };

  const nativeMin = formatNativeBoundary(rest?.minDate)
    || (rest?.disablePast ? moment().format(nativeFormat) : '');
  const nativeMax = formatNativeBoundary(rest?.maxDate)
    || (rest?.disableFuture ? moment().format(nativeFormat) : '');
  const NativeIcon = onlyDate ? EventIcon : AccessTimeIcon;
  const pickerValue = selectedValue
    ? parseInStoredTimezone(selectedValue, dateFormatSting)
    : null;

  const isValueEmpty = (value) => {
    if (Array.isArray(value)) {
      return value.length === 0;
    }
    if (value == null) {
      return true;
    }
    if (typeof value === 'string') {
      return value.trim() === '';
    }
    return false;
  };

  useEffect(() => {
    if (shouldPrefillFromShift && !shiftLoaded) {
      dispatch(getShiftDetails());
    }
  }, [dispatch, shiftLoaded, shouldPrefillFromShift]);

  useEffect(() => {
    if (shouldPrefillFromShift && currentShift?.shift_starts_from && !selectedValue) {
      if (name.includes('gte')) {
        const startTime = moment(currentShift.shift_starts_from).tz(userTimezone);
        const startDate = moment()
          .tz(userTimezone)
          .hour(startTime.hour())
          .minute(startTime.minute())
          .seconds(0);
        const dateString = startDate.format(dateFormatSting);
        formikProps.setFieldValue(name, dateString, true);
      } else if (name.includes('lte')) {
        const startTime = moment(currentShift.shift_starts_from).tz(userTimezone);
        const startDate = moment()
          .tz(userTimezone)
          .hour(startTime.hour())
          .minute(startTime.minute())
          .seconds(0);
        const endDate = startDate.add(currentShift.shift_duration, 'hours');
        const dateString = endDate.format(dateFormatSting);
        formikProps.setFieldValue(name, dateString, true);
      }
    }
  }, [currentShift, dateFormatSting, formikProps, name, selectedValue, shouldPrefillFromShift]);

  useEffect(() => {
    if (!required || shouldPrefillFromShift || selectedValue || hasInitializedDefault.current) return;

    hasInitializedDefault.current = true;
    formikProps.setFieldValue(
      name,
      moment()
        .tz(userTimezone)
        .seconds(0)
        .milliseconds(0)
        .format(dateFormatSting),
      false
    );
  }, [dateFormatSting, formikProps, name, required, selectedValue, shouldPrefillFromShift]);

  const onDateChange = (date) => {
    if (!date || !moment(date).isValid()) {
      return;
    }

    const dateString = moment(date).format(dateFormatSting);
    formikProps.setFieldValue(name, dateString, true);
  };

  function renderHelperText() {
    if (meta.touched && meta.error) {
      return meta.error;
    }
    if (required && isValueEmpty(selectedValue)) {
      return requiredHintText;
    }
    return null;
  }

  const PickerComponent = onlyDate ? DatePicker : DateTimePicker;
  const pickerSpecificProps = onlyDate
    ? {}
    : {
      ampm: true,
      views: ['year', 'month', 'day', 'hours', 'minutes']
    };

  if (nativeInput && onlyDate) {
    let helperText = rest?.helperText || (onlyDate ? 'Select a date' : 'Select date and time');
    if (required && isValueEmpty(selectedValue)) {
      helperText = requiredHintText;
    }
    if (meta.touched && meta.error) {
      helperText = meta.error;
    }

    return (
      <Stack spacing={0.75}>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ letterSpacing: 0.4, textTransform: 'uppercase' }}
        >
          {label}
        </Typography>

        <TextField
          label={null}
          type={onlyDate ? 'date' : 'datetime-local'}
          value={formatNativeValue(selectedValue)}
          onChange={handleNativeChange}
          disabled={disabled}
          error={Boolean(meta.touched && meta.error)}
          helperText={helperText}
          InputLabelProps={{
            shrink: true,
            ...(restInputLabelProps || {})
          }}
          InputProps={{
            ...(restInputProps || {}),
            startAdornment: (
              <InputAdornment position="start" sx={{ color: 'text.secondary' }}>
                <NativeIcon fontSize="small" />
              </InputAdornment>
            ),
            inputProps: {
              ...(nativeMin ? { min: nativeMin } : {}),
              ...(nativeMax ? { max: nativeMax } : {}),
              ...(onlyDate ? {} : { step: 60 }),
              ...(restInputAttributes || {})
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              backgroundColor: disabled ? colors.grey[200] : '#fff',
              transition: 'all 0.2s ease',
              '& fieldset': {
                borderColor: 'rgba(15, 23, 42, 0.12)'
              },
              '&:hover fieldset': {
                borderColor: 'rgba(15, 23, 42, 0.28)'
              },
              '&.Mui-focused fieldset': {
                borderColor: 'primary.main',
                borderWidth: 1.5
              }
            },
            '& .MuiInputBase-input': {
              fontWeight: 400
            },
            '& .MuiFormHelperText-root': {
              ml: 0.5,
              mt: 1
            },
            ...(restSx || {})
          }}
          fullWidth={rest.fullWidth !== false}
        />
      </Stack>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Stack spacing={3}>
        <PickerComponent
          {...field}
          {...rest}
          {...pickerSpecificProps}
          onChange={onDateChange}
          label={label}
          inputFormat={pickerDisplayFormat}
          value={pickerValue}
          readOnly={disabled}
          disableOpenPicker={disableOpenPicker}
          renderInput={(props) => (
            <TextField
              {...props}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  backgroundColor: disabled ? colors.grey[200] : '#fff',
                  transition: 'all 0.2s ease',
                  '& fieldset': {
                    borderColor: 'rgba(15, 23, 42, 0.12)'
                  },
                  '&:hover fieldset': {
                    borderColor: 'rgba(15, 23, 42, 0.28)'
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: 'primary.main',
                    borderWidth: 1.5
                  }
                },
                '& .MuiInputBase-input': {
                  fontWeight: 400
                },
                ...(restSx || {})
              }}
              disabled={disabled}
              error={Boolean(meta.touched && meta.error)}
              helperText={renderHelperText()}
            />
          )}
        />
      </Stack>
    </LocalizationProvider>
  );
}

DatePickerField.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  dateFormat: PropTypes.string,
  disabled: PropTypes.bool,
  onlyDate: PropTypes.bool,
  displayFormat: PropTypes.string,
  disableShiftPrefill: PropTypes.bool,
  disableOpenPicker: PropTypes.bool,
  nativeInput: PropTypes.bool,
  required: PropTypes.bool,
  requiredHintText: PropTypes.string
};
